import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  QUOTE_REQUEST_NAME_COLUMN_WIDTH,
  type QuoteRequestsTableData,
} from "#app/routes/pages/quote-requests/QuoteRequestsV2.page";
import { radius } from "#app/theme/v2/radius";
import { spacing } from "#app/theme/v2/spacing";
import { Skeleton, TextSkeleton } from "#components/atoms/Skeleton/Skeleton";
import { createTableColumnHelper } from "#components/organisms/Table/tableHook";
import { QuoteRequestNameCell } from "#modules/quoteRequestsV2/view/organisms/QuoteRequestNameCell";
import { QuoteRequestPartsCell } from "#modules/quoteRequestsV2/view/organisms/QuoteRequestPartsCell";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { formatDate } from "shared";

const PARTS_SKELETON_COUNT = 6;
const PARTS_THUMBNAIL_SIZE = 48;
const QUOTE_REQUEST_CREATED_AT_COLUMN_WIDTH = 240;

const columnHelper = createTableColumnHelper<QuoteRequestsTableData>();

const QuoteRequestNameCellSkeleton = () => (
  <Stack w={QUOTE_REQUEST_NAME_COLUMN_WIDTH} gap={spacing[1]}>
    <TextSkeleton size="small" width="100%" />
    <TextSkeleton size="tiny" width={160} />
  </Stack>
);

const QuoteRequestPartsCellSkeleton = () => (
  <Flex gap={spacing[2]}>
    {Array.from({ length: PARTS_SKELETON_COUNT }).map((_, index) => (
      <Skeleton
        key={index}
        variant="rectangle"
        width={PARTS_THUMBNAIL_SIZE}
        height={PARTS_THUMBNAIL_SIZE}
        radius={radius.sm}
      />
    ))}
  </Flex>
);

export const useQuoteRequestsV2Columns = (isLoading: boolean) => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      columnHelper.display({
        id: "name",
        header: t("quoteRequestsV2.table.columns.name"),
        size: QUOTE_REQUEST_NAME_COLUMN_WIDTH,
        cell: ({ row }) =>
          isLoading ? (
            <QuoteRequestNameCellSkeleton />
          ) : (
            <QuoteRequestNameCell quoteRequest={row.original} />
          ),
      }),
      columnHelper.display({
        id: "parts",
        header: t("quoteRequestsV2.table.columns.parts"),
        meta: {
          width: `calc(100% - ${QUOTE_REQUEST_NAME_COLUMN_WIDTH}px - ${QUOTE_REQUEST_CREATED_AT_COLUMN_WIDTH}px)`,
        },
        cell: ({ row }) =>
          isLoading ? (
            <QuoteRequestPartsCellSkeleton />
          ) : (
            <QuoteRequestPartsCell quoteRequest={row.original} />
          ),
      }),
      columnHelper.display({
        id: "createdAt",
        header: t("quoteRequestsV2.table.columns.createdAt"),
        size: QUOTE_REQUEST_CREATED_AT_COLUMN_WIDTH,
        cell: ({ row }) =>
          isLoading ? (
            <TextSkeleton size="small" width={80} />
          ) : (
            <Flex gap={spacing[2]} wrap="nowrap">
              {formatDate(row.original.createdAt)}
            </Flex>
          ),
      }),
    ],
    [t, isLoading],
  );
};
